import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider } from 'styled-components';
import {
  DASHBOARD_THEME_STORAGE_KEY,
  darkDashboardTheme,
  lightDashboardTheme,
} from '../theme/dashboardTheme';

const DashboardThemeContext = createContext(null);

export function DashboardThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    try {
      const stored = localStorage.getItem(DASHBOARD_THEME_STORAGE_KEY);
      return stored === 'dark' ? 'dark' : 'light';
    } catch {
      return 'light';
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem(DASHBOARD_THEME_STORAGE_KEY, mode);
    } catch {
      /* ignore */
    }
  }, [mode]);

  const toggleTheme = useCallback(() => {
    setMode((m) => (m === 'dark' ? 'light' : 'dark'));
  }, []);

  const themeObject = useMemo(
    () => (mode === 'dark' ? darkDashboardTheme : lightDashboardTheme),
    [mode]
  );

  const value = useMemo(
    () => ({ mode, setMode, toggleTheme }),
    [mode, toggleTheme]
  );

  return (
    <DashboardThemeContext.Provider value={value}>
      <ThemeProvider theme={themeObject}>{children}</ThemeProvider>
    </DashboardThemeContext.Provider>
  );
}

export function useDashboardTheme() {
  const ctx = useContext(DashboardThemeContext);
  if (!ctx) {
    throw new Error('useDashboardTheme must be used within DashboardThemeProvider');
  }
  return ctx;
}
